import type {
  Address,
  DetailResponse,
  Feature,
  GoogleAddress,
  HubData,
  LatLng,
  SavedPlacesData,
} from '@/api/place/types';
import { GET_CURRENT_LOCATION_MAX_AGE } from './constants';

export function checkGeolocationPermission() {
  return new Promise((resolve) => {
    // return true cause we cannot query permission
    if (!navigator.permissions || !navigator.permissions.query)
      return resolve(true);

    // return true cause exceed timeout
    setTimeout(() => {
      return resolve(true);
    }, 1000);

    navigator.permissions.query({ name: 'geolocation' }).then((result) => {
      if (result.state === 'denied') return resolve(false);

      return resolve(true);
    });
  });
}

export const getCurrentPosition = (): Promise<LatLng> =>
  new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          if (!lat || !lng) return reject();

          return resolve({ lat, lng });
        },
        (error) => {
          console.log('GetCurrentLocation => Error: ', error);
          return reject(error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: GET_CURRENT_LOCATION_MAX_AGE,
          // timeout: GET_CURRENT_LOCATION_TIMEOUT,
        }
      );
    } else {
      reject('Geolocation is not supported by this browser');
    }
  });

export const featureToAddress = (feature: Feature): Address | null => {
  try {
    if (
      feature &&
      feature.properties &&
      feature.properties.name &&
      feature.geometry &&
      feature.geometry.coordinates &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length > 1
    ) {
      const addressMaterial =
        feature && feature.properties && feature.properties.name?.split(',');
      const first_label =
        (addressMaterial && addressMaterial.length > 0 && addressMaterial[0]) ||
        (feature && feature.properties && feature.properties.name);
      const addressMaterialNotFirstLabel =
        (addressMaterial &&
          addressMaterial.length > 0 &&
          addressMaterial.slice(1, addressMaterial.length)) ||
        (feature && feature.properties && feature.properties.name);
      const second_label =
        (feature && feature.properties && feature.properties.address) ||
        (addressMaterialNotFirstLabel &&
          Array.isArray(addressMaterialNotFirstLabel) &&
          addressMaterialNotFirstLabel.length > 1 &&
          addressMaterialNotFirstLabel.join(', ')) ||
        (feature && feature.properties && feature.properties.name);
      return {
        first_label,
        second_label,
        address:
          (feature && feature.properties && feature.properties.address) ||
          (feature && feature.properties && feature.properties.name),
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0],
        short_address: first_label,
      };
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const convertAddress = (address: Address): Address | null => {
  try {
    if (address && address.address) {
      const addressMaterial = address && address.address?.split(',');
      const first_label =
        (addressMaterial && addressMaterial.length > 0 && addressMaterial[0]) ||
        (address && address.address);
      const addressMaterialNotFirstLabel =
        (addressMaterial &&
          addressMaterial.length > 0 &&
          addressMaterial.slice(1, addressMaterial.length)) ||
        (address && address.address);
      const second_label =
        (address && address.address) ||
        (addressMaterialNotFirstLabel &&
          Array.isArray(addressMaterialNotFirstLabel) &&
          addressMaterialNotFirstLabel.length > 1 &&
          addressMaterialNotFirstLabel.join(', ')) ||
        (address && address.address);
      return {
        first_label,
        second_label,
        address: address && address.address,
        lat: address.lat,
        lng: address.lng,
        short_address: first_label,
      };
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const savedPlaceToAddress = (
  savedplace: SavedPlacesData
): Address | null => {
  try {
    if (
      savedplace &&
      savedplace.address &&
      savedplace.location &&
      savedplace.location.coordinates &&
      Array.isArray(savedplace.location.coordinates) &&
      savedplace.location.coordinates.length > 1
    ) {
      const addressMaterial = savedplace.address.split(',');
      const addressMaterialNotFirstLabel =
        (addressMaterial &&
          addressMaterial.length > 0 &&
          addressMaterial.slice(1, addressMaterial.length)) ||
        savedplace.address;
      const second_label =
        (Array.isArray(addressMaterialNotFirstLabel) &&
          addressMaterialNotFirstLabel.length > 1 &&
          addressMaterialNotFirstLabel.join(', ')) ||
        savedplace.address;
      return {
        first_label: savedplace.name,
        second_label,
        address: savedplace.address,
        lat:
          savedplace.location.coordinates[0] < 90 &&
          savedplace.location.coordinates[0] > -90
            ? savedplace.location.coordinates[0]
            : savedplace.location.coordinates[1],
        lng:
          savedplace.location.coordinates[0] < 90 &&
          savedplace.location.coordinates[0] > -90
            ? savedplace.location.coordinates[1]
            : savedplace.location.coordinates[0],
        short_address: savedplace.name,
      };
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const featureToGoogleAddress = (
  feature: Feature
): GoogleAddress | null => {
  try {
    if (feature && feature.properties && feature.properties.name) {
      const addressMaterial = feature.properties.name?.split(',');
      const first_label =
        (addressMaterial && addressMaterial.length > 0 && addressMaterial[0]) ||
        feature.properties.name;
      const addressMaterialNotFirstLabel =
        (addressMaterial &&
          addressMaterial.length > 0 &&
          addressMaterial.slice(1, addressMaterial.length)) ||
        feature.properties.name;
      const second_label =
        feature.properties.address ||
        (addressMaterialNotFirstLabel &&
          Array.isArray(addressMaterialNotFirstLabel) &&
          addressMaterialNotFirstLabel.length > 1 &&
          addressMaterialNotFirstLabel.join(', ')) ||
        feature.properties.name;
      return {
        first_label,
        second_label,
        address: feature.properties.address || feature.properties.name,
        place_id: feature.properties.place_id,
        sessiontoken: feature.properties.sessiontoken,
        short_address: first_label,
      };
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const detailResponseToAddress = (params: DetailResponse): Address => {
  try {
    if (
      params &&
      params.formatted_address &&
      params.geometry &&
      params.geometry.location &&
      Array.isArray(params.geometry.location) &&
      params.geometry.location.length > 1
    ) {
      const addressMaterial = params.formatted_address;
      const first_label =
        (addressMaterial && addressMaterial.length > 0 && addressMaterial[0]) ||
        params.formatted_address;
      const addressMaterialNotFirstLabel =
        (addressMaterial &&
          addressMaterial.length > 0 &&
          addressMaterial.slice(1, addressMaterial.length)) ||
        params.formatted_address;
      const second_label =
        (addressMaterialNotFirstLabel &&
          Array.isArray(addressMaterialNotFirstLabel) &&
          addressMaterialNotFirstLabel.length > 1 &&
          addressMaterialNotFirstLabel.join(', ')) ||
        params.formatted_address;
      return {
        first_label,
        second_label,
        address: params.formatted_address,
        lat: params.geometry.location.lat,
        lng: params.geometry.location.lng,
        short_address: first_label,
      };
    }

    return {} as Address;
  } catch (e) {
    return {} as Address;
  }
};

export const isGoogleAddress = (
  object: GoogleAddress
): object is GoogleAddress => {
  return 'place_id' in object;
};

export const isAddress = (object: Address): object is Address => {
  return 'address' in object;
};

export const hubToAddress = (hub: HubData): Address => {
  const addressMaterial = hub.address?.split(',');
  const first_label = hub.name;
  const addressMaterialNotFirstLabel =
    (addressMaterial &&
      addressMaterial.length > 0 &&
      addressMaterial.slice(1, addressMaterial.length)) ||
    hub.address;
  const second_label =
    hub.address ||
    (addressMaterialNotFirstLabel &&
      Array.isArray(addressMaterialNotFirstLabel) &&
      addressMaterialNotFirstLabel.length > 1 &&
      addressMaterialNotFirstLabel.join(', ')) ||
    hub.address;

  return {
    first_label,
    second_label,
    address: hub.address || hub.address,
    lat: hub.location.coordinates[1],
    lng: hub.location.coordinates[0],
    short_address: first_label,
  };
};

export const convertBankCardNumber = (name: string) => {
  return `*** ${name.slice(-4)}`;
};

export function objectToQuery(obj: {
  [key: string]: string | string[] | number | undefined;
}) {
  let query = '';
  for (const key in obj) {
    if (obj[key]) {
      const value = obj[key];
      if (Array.isArray(value)) {
        value.forEach(function (item) {
          query += key + '=' + encodeURIComponent(item) + '&';
        });
      } else {
        query += key + '=' + encodeURIComponent(value) + '&';
      }
    }
  }
  return query.slice(0, -1);
}
